<div class="page-container">
  <intelishs-page-header
    title="Mis Autorizaciones"
    subtitle="Solicitudes de vacaciones de tu equipo"
  />

  <!-- Selector administrativo: solo visible para Admin/RH. Director se
       autoselecciona vía /api/Empleado/me cruzado contra la lista de
       directores y este bloque queda oculto. -->
  @if (puedeSeleccionarOtro()) {
    <div class="employee-selector">
      <nz-icon nzType="info-circle" class="text-muted" />
      <small class="text-muted">
        Vista administrativa — selecciona el director cuyas autorizaciones quieres revisar
      </small>
      <nz-select
        style="width: 100%; margin-top: 8px"
        nzShowSearch
        nzPlaceHolder="Seleccionar director"
        [ngModel]="directorSeleccionado()?.directorId ?? null"
        (ngModelChange)="onDirectorChange($event)"
      >
        @for (dir of directores(); track dir.directorId) {
          <nz-option
            [nzValue]="dir.directorId"
            [nzLabel]="dir.directorIdInterno + ' - ' + dir.directorNombre"
          />
        }
      </nz-select>
      @if (directorSeleccionado(); as dir) {
        <div style="margin-top: 8px; font-size: 12px; color: #888">
          @if (dir.esDG) {
            <nz-icon nzType="crown" style="color: #faad14; margin-right: 4px" />
            Director General — supervisa las vacaciones de
            {{ dir.directorEmpleadoIds?.length ?? 0 }} directores de área
          } @else {
            Áreas asignadas: {{ dir.areas.join(', ') }}
          }
        </div>
      }
    </div>
  }

  @if (directorSeleccionado()) {
    <!-- Templates para los títulos de las pestañas -->
    <ng-template #tabEquipoTpl>
      <intelishs-tab-label
        label="Mi Equipo"
        [count]="equipo().length"
        [badgeVisible]="!loadingEquipo() && equipo().length > 0"
        badgeColor="#1890ff"
      />
    </ng-template>

    <ng-template #tabSolicitudesTpl>
      <intelishs-tab-label label="Solicitudes pendientes" [count]="totalPendientes()" />
    </ng-template>

    <nz-tabs [nzAnimated]="true" nzSize="default">
      <!-- ══ TAB 1: MI EQUIPO ══════════════════════════════════════════ -->
      <nz-tab [nzTitle]="tabEquipoTpl">
        <div class="mi-equipo">
          <!-- Filtros por contadores. Usan el chip de la librería, igual que
               empleados y vacaciones-lista: esta era la única pantalla con
               chips propios. El icono desaparece porque el punto de conteo y el
               tono ya distinguen cada filtro; la urgencia la comunica el color
               (rojo cuando hay casos críticos, ámbar si no). -->
          <div class="stats-row">
            <intelishs-stat-chip
              color="dark"
              [value]="equipo().length"
              label="empleados"
              [active]="filtroEstado() === 'todos'"
              (select)="filtroEstado.set('todos')"
            />
            @if (equipoStats().porVencer > 0) {
              <span nz-tooltip nzTooltipTitle="Empleados con días que vencen en menos de 30 días">
                <intelishs-stat-chip
                  [color]="equipoStats().criticos > 0 ? 'red' : 'orange'"
                  [value]="equipoStats().porVencer"
                  label="por vencer"
                  [active]="filtroEstado() === 'por-vencer'"
                  (select)="
                    filtroEstado.set(filtroEstado() === 'por-vencer' ? 'todos' : 'por-vencer')
                  "
                />
              </span>
            }
            @if (equipoStats().conPendientes > 0) {
              <span
                nz-tooltip
                nzTooltipTitle="Empleados con solicitudes pendientes de tu aprobación"
              >
                <intelishs-stat-chip
                  color="blue"
                  [value]="equipoStats().conPendientes"
                  label="con pendientes"
                  [active]="filtroEstado() === 'pendientes'"
                  (select)="
                    filtroEstado.set(filtroEstado() === 'pendientes' ? 'todos' : 'pendientes')
                  "
                />
              </span>
            }
          </div>

          <!-- Tabla del equipo -->
          <intelishs-data-table
            [data]="equipoFiltrado()"
            [columns]="equipoColumns"
            [loading]="loadingEquipo()"
            [pageSize]="10"
            [rowClassFn]="equipoRowClass"
            searchPlaceholder="Buscar por nombre, ID o puesto..."
            [extraFilterKeys]="equipoFilterKeys"
            [showRefreshButton]="true"
            (refresh)="cargarEquipo()"
          >
            <!-- Empleado: ID interno se mueve a su propia columna; aquí
                 solo nombre + área para no duplicar. -->
            <ng-template intelishsCellTemplate="empleadoNombre" let-emp>
              <intelishs-cell-stack
                [main]="emp.empleadoNombre"
                [meta]="emp.idInterno + (emp.puesto ? ' · ' + emp.puesto : '')"
              />
            </ng-template>

            <!-- Ciclo anterior -->
            <ng-template intelishsCellTemplate="periodoAnterior" let-emp>
              @if (emp.periodoAnterior; as pa) {
                <div
                  class="equipo-venc"
                  [class.equipo-venc--critico]="urgenciaEmp(emp) === 'critico'"
                  [class.equipo-venc--advertencia]="urgenciaEmp(emp) === 'advertencia'"
                >
                  <div class="equipo-venc-dias">
                    <strong>{{ pa.diasDisponibles | number: '1.0-1' }}</strong>
                    <span style="font-size: 11px; color: #8c8c8c"> días</span>
                  </div>
                  <div class="equipo-venc-fecha">
                    Vence: {{ pa.fechaVencimiento | date: 'dd/MM/yyyy' }}
                  </div>
                  @if (urgenciaEmp(emp)) {
                    <div class="equipo-venc-contador">
                      ⚠ {{ diasHastaVencimientoEmp(emp) }} días restantes
                    </div>
                  }
                </div>
              } @else if (!emp.desbloqueado) {
                <span class="equipo-bloqueado">
                  <nz-icon nzType="lock" />
                  No disponible aún
                </span>
              } @else {
                <span class="equipo-sin-datos">Sin ciclo anterior</span>
              }
            </ng-template>

            <!-- Ciclo actual -->
            <ng-template intelishsCellTemplate="periodoActual" let-emp>
              @if (emp.periodoActual; as pc) {
                <div class="equipo-actual">
                  <strong>{{ pc.diasGenerados | number: '1.1-1' }}</strong>
                  <span class="equipo-actual-total"> de {{ pc.diasCorrespondientes }}</span>
                </div>
                <div class="equipo-mini-progreso">
                  <div
                    class="equipo-mini-progreso-fill"
                    [style.width.%]="
                      pc.diasCorrespondientes > 0
                        ? (pc.diasGenerados / pc.diasCorrespondientes) * 100
                        : 0
                    "
                  ></div>
                </div>
              } @else {
                <span class="equipo-sin-datos">—</span>
              }
            </ng-template>

            <!-- Total disponible -->
            <ng-template intelishsCellTemplate="diasDisponiblesTotal" let-emp>
              <span
                class="equipo-total"
                [class.equipo-total--negativo]="emp.tieneSaldoNegativo"
                nz-tooltip
                [nzTooltipTitle]="
                  emp.tieneSaldoNegativo
                    ? 'Saldo negativo — se compensará en el futuro'
                    : 'Total disponible'
                "
              >
                {{ emp.diasDisponiblesTotal | number: '1.1-1' }}
              </span>
            </ng-template>

            <!-- Vacaciones aprobadas -->
            <ng-template intelishsCellTemplate="proximasVacaciones" let-emp>
              <!-- Rango arriba, duración debajo, con el mismo `cell-stack` de la
                   columna Empleado. Antes era una pastilla verde con borde e
                   icono: el único elemento con caja de toda la tabla, donde el
                   resto de columnas usa texto con jerarquía, cifras en color y
                   una barra de progreso. Y el verde no aportaba —la columna se
                   llama "aprobadas", así que todo lo que aparece aquí lo está—.
                   La duración sube del tooltip a la línea meta: se lee sin
                   tener que apuntar el mouse. -->
              @if (getProximasVacacionesVisibles(emp).length > 0) {
                <div class="equipo-vacaciones-aprobadas">
                  @for (vac of getProximasVacacionesVisibles(emp); track vac.id) {
                    <intelishs-cell-stack
                      [main]="
                        (vac.fechaInicio | date: 'dd/MM/yyyy') +
                        ' → ' +
                        (vac.fechaFin | date: 'dd/MM/yyyy')
                      "
                      [meta]="
                        vac.diasSolicitados +
                        (vac.diasSolicitados === 1 ? ' día hábil' : ' días hábiles')
                      "
                    />
                  }
                </div>
              } @else {
                <span class="equipo-sin-datos">—</span>
              }
            </ng-template>

            <!-- Acciones -->
            <ng-template intelishsCellTemplate="acciones" let-emp>
              <intelishs-table-actions
                [row]="emp"
                [actions]="getActionsForEquipo(emp)"
                (action)="onEquipoAction({ action: $event, row: emp })"
              />
            </ng-template>
          </intelishs-data-table>
        </div>
      </nz-tab>

      <!-- ══ TAB 2: SOLICITUDES PENDIENTES ════════════════════════════ -->
      <nz-tab [nzTitle]="tabSolicitudesTpl">
        <!-- Sin chip de conteo: la pestaña ya lleva el número en su badge, y
             aquí no filtraba nada —era un contador informativo repitiendo lo
             que dice la etiqueta de arriba—. -->
        @if (solicitudes().length === 0 && !loading()) {
          <intelishs-empty-state
            variant="card"
            icon="check-circle"
            iconColor="#52c41a"
            title="Sin solicitudes pendientes"
            message="No hay solicitudes de tu equipo que requieran tu aprobación"
          />
        } @else {
          <intelishs-data-table
            [data]="solicitudes()"
            [columns]="solicitudesColumns"
            [loading]="loading()"
            [pageSize]="15"
            [pageSizeOptions]="[10, 15, 25, 50]"
            searchPlaceholder="Buscar por nombre, ID, área o puesto..."
            [extraFilterKeys]="solicitudesFilterKeys"
            [showRefreshButton]="true"
            (refresh)="cargar()"
          >
            <!-- Empleado con badge saldo negativo -->
            <!-- El badge de alerta va al lado, no dentro de la celda de dos
                 líneas: es una alerta sobre la fila, no contexto del nombre. -->
            <ng-template intelishsCellTemplate="empleadoNombre" let-sol>
              <div class="celda-empleado">
                <intelishs-cell-stack
                  [main]="sol.empleadoNombre"
                  [meta]="sol.empleadoIdInterno + (sol.puesto ? ' · ' + sol.puesto : '')"
                />
                @if (solicitudGeneraSaldoNegativo(sol.empleadoId)) {
                  <span
                    class="badge-saldo-negativo"
                    nz-tooltip
                    nzTooltipTitle="Esta solicitud genera saldo negativo de días"
                    >⚠️</span
                  >
                }
              </div>
            </ng-template>

            <!-- Periodo: el rango se lee junto y los días hábiles dan contexto -->
            <ng-template intelishsCellTemplate="fechaInicio" let-sol>
              <intelishs-cell-stack
                [main]="
                  (sol.fechaInicio | date: 'dd/MM/yyyy') +
                  ' → ' +
                  (sol.fechaFin | date: 'dd/MM/yyyy')
                "
                [meta]="
                  sol.diasSolicitados + (sol.diasSolicitados === 1 ? ' día hábil' : ' días hábiles')
                "
              />
            </ng-template>

            <!-- Acciones -->
            <ng-template intelishsCellTemplate="acciones" let-sol>
              <intelishs-table-actions
                [row]="sol"
                [actions]="solicitudesActions"
                (action)="onSolicitudAction({ action: $event, row: sol })"
              />
            </ng-template>
          </intelishs-data-table>
        }
      </nz-tab>
    </nz-tabs>
  }
</div>

<!-- ══ Modal detalle de solicitud / saldo ════════════════════════════ -->
<app-solicitud-detalle-modal
  [(visible)]="detalleVisible"
  [solicitud]="detalleEmpleado()"
  modo="director"
  [soloSaldo]="detalleEsSoloSaldo()"
/>

<!-- ══ Modal aprobación ══════════════════════════════════════════════ -->
<app-solicitud-aprobar-modal
  [(visible)]="modalVisible"
  [solicitud]="solicitudSeleccionada()"
  [accion]="modalAccion()"
  paso="director"
  (procesado)="onAprobacionProcesada()"
/>
